import { PowerArchitecture, PrimaryWireName, SecondaryWireName, TransformerSize } from "fond/types";
import { convertFeetToMeters } from "fond/utils";

export type TransformerSizeDescriptor = {
  Size: TransformerSize;
  // Nominal rating in kVA, e.g. 15.
  RatingKva: number;
  // Value the row's input is seeded with while the size is disabled.
  DefaultAverageAddressCount: number;
};

export type PrimaryWireDescriptor = {
  Name: PrimaryWireName;
  // Wire size as displayed to the user, e.g. "2x_1/0_Al".
  Label: string;
  PhaseCount: number;
  Description: string;
  // Value the row's input is seeded with while the wire is disabled.
  DefaultMaxLoadAmps: number;
};

export type SecondaryWireDescriptor = {
  Name: SecondaryWireName;
  // Wire size as displayed to the user, e.g. "4/0".
  Label: string;
  Description: string;
  // Value the row's input is seeded with while the wire is disabled.
  DefaultAverageAddressCount: number;
};

export const TRANSFORMER_SIZE_OPTIONS: TransformerSizeDescriptor[] = [
  { Size: "kva_15", RatingKva: 15, DefaultAverageAddressCount: 1 },
  { Size: "kva_25", RatingKva: 25, DefaultAverageAddressCount: 2 },
  { Size: "kva_50", RatingKva: 50, DefaultAverageAddressCount: 4 },
  { Size: "kva_75", RatingKva: 75, DefaultAverageAddressCount: 5 },
];

export const PRIMARY_WIRE_OPTIONS: PrimaryWireDescriptor[] = [
  { Name: "awg_1_0_al_1ph", Label: "1/0_Al", PhaseCount: 1, Description: "Single-phase one-aught aluminum wire", DefaultMaxLoadAmps: 30 },
  { Name: "awg_1_0_al_2ph", Label: "2x_1/0_Al", PhaseCount: 2, Description: "Two-phase one-aught aluminum wire", DefaultMaxLoadAmps: 150 },
  { Name: "awg_1_0_al_3ph", Label: "3x_1/0_Al", PhaseCount: 3, Description: "Three-phase one-aught aluminum wire", DefaultMaxLoadAmps: 300 },
];

export const SECONDARY_WIRE_OPTIONS: SecondaryWireDescriptor[] = [
  {
    Name: "awg_4_0",
    Label: "4/0",
    Description: "Used for connection to a meter - from a transformer or a pull box",
    DefaultAverageAddressCount: 1,
  },
  {
    Name: "kcmil_350",
    Label: "350",
    Description: "If enabled, pull boxes will be placed where breakouts to 4/0 wire are needed to reach meters",
    DefaultAverageAddressCount: 3,
  },
];

/**
 * The architecture a new MU_POWER configuration form is pre-filled with.
 */
export const DEFAULT_MU_POWER_ARCHITECTURE: PowerArchitecture = {
  Type: "power",
  Transformers: {
    AverageMeterLoadAmps: 60,
    Options: {
      kva_15: null,
      kva_25: { AverageAddressCount: 2 },
      kva_50: { AverageAddressCount: 4 },
      kva_75: null,
    },
  },
  PrimaryWires: {
    VoltageVolts: 14400,
    MaxSourceLoadAmps: 140,
    Options: {
      awg_1_0_al_1ph: { MaxLoadAmps: 30 },
      awg_1_0_al_2ph: null,
      awg_1_0_al_3ph: { MaxLoadAmps: 300 },
    },
  },
  SecondaryWires: {
    MaxLengthMetres: convertFeetToMeters(200),
    Options: {
      awg_4_0: { AverageAddressCount: 1 },
      kcmil_350: { AverageAddressCount: 3 },
    },
  },
};
